import { debug } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/debug.js';
import { locales as commonLocales } from './internal-locales.js';
import { merge } from './merge.js';
/**
 *
 * Usage (declare directly below your class declaration):
 *
 * Single Object
 *
 * `private i18n = new InternalI18n(this, locales);`
 *
 * Array of Objects
 *
 * `private i18n = new InternalI18n(this, locales, sharedLocales);`
 *
 * Adding Locale Data Inline
 *
 * `private i18n = new InternalI18n(this, locales, sharedLocales, { 'fr': { show: 'fr show', hide: 'fr hide' } });`
 *
 * Testing:
 *
 * Set `document.documentElement.setAttribute('lang', 'your-lang')` in your code.
 */
export class InternalI18n {
    // TODO: change params (host, { resources: [], debug, ... })
    constructor(host, ...locales) {
        this.host = host;
        this.supportedLngs = ['en-us', 'es-us'];
        /**
         * Get debugging output printed to the browser's console.
         *
         * Usage:
         *
         * `updated() {
         *   this.i18n.debug();
         * }`
         */
        this.debug = () => {
            if (debug()) {
                const name = `${this.constructor.name}:${this.host.constructor.name}`;
                console.log(`[${name}] lookup data (all registered locales):`, this.lookup);
                console.log(`[${name}] locale data for '${this.lang}':`, this.localeData);
                console.log('\n');
            }
        };
        /**
         * Get locale data.
         *
         * @param locales An array of objects containing a mapping of `locale` data.
         *
         * @returns a new object of current `locale` data.
         */
        this.registerLocales = (...locales) => merge(...locales);
        /**
         * Translate text using a provided key.
         * @param key The translation key.
         * @param options currently only `count` option is supported. The pluralization number. `1` matches `key`. `0` or `n >= 2` matches `key_plural`.
         * @returns The text.
         *
         * Example:
         *
         * `this.i18n.t('element', { count: 2 })`
         *
         * Outputs:
         *
         * `2 items`
         *
         * Schema:
         *
         * `{
         *   "element": "{{count}} item",
         *   "element_plural": "{{count}} items"
         * }`
         *
         * **Note:** Error logging behavior similar to 18next.
         */
        this.t = (key, options) => {
            if (!key) {
                const keyNotProvidedError = new Error('Key not provided.');
                console.log(keyNotProvidedError.stack);
                return '';
            }
            if ((options === null || options === void 0 ? void 0 : options.count) !== undefined) {
                if (new Intl.PluralRules().select(options.count) === 'other') {
                    key = `${key}_plural`;
                }
            }
            if (this.localeData === undefined) {
                const localeNotFoundError = new Error('Unable to look up locale and locale data.');
                console.log(localeNotFoundError.stack);
                return key;
            }
            if (!(key in this.localeData)) {
                const keyNotFoundError = new Error(`Unable to find key '${key}' in '${this.lang}'.`);
                console.log(keyNotFoundError.stack);
                return key;
            }
            return (options === null || options === void 0 ? void 0 : options.count) !== undefined ? this.localeData[key].replace('{{count}}', `${options === null || options === void 0 ? void 0 : options.count}`) : this.localeData[key];
        };
        // Store a reference to the host and register for lifecycle updates
        this.host.addController(this);
        this.lang = this.normalizeLang(this.detectLanguage());
        this.lookup = this.registerLocales(commonLocales, ...locales);
        this.localeData = this.lookup[this.lang];
    }
    hostConnected() {
        this.debug(); // disabling for now as this is loud
    }
    hostDisconnected() {
        this.host.removeController(this);
    }
    changeLanguage(lang) {
        this.lang = this.normalizeLang(lang);
        this.localeData = this.lookup[this.lang];
        this.host.requestUpdate();
    }
    detectLanguage() {
        // allow a quick way to test
        if (process.env.NODE_ENV !== 'production') {
            const params = new URLSearchParams(location.search);
            if (params.has('lng')) {
                return params.get('lng');
            }
        }
        return document.documentElement.lang;
    }
    // if lng = 'en' then return 'en-us', similar to i18next
    normalizeLang(lng) {
        lng = lng.toLowerCase();
        if (this.supportedLngs.includes(lng)) {
            return lng;
        }
        const [lngOnly, ..._rest] = lng.split('-');
        const found = this.supportedLngs.find((supportedLng) => supportedLng.includes(lngOnly));
        return (found === null || found === void 0 ? void 0 : found.toLowerCase()) || this.supportedLngs[0];
    }
}
//# sourceMappingURL=internal-i18n-controller.js.map